import { useRef, useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

export type FaseOverlayAniversario = 'countdown-inicial' | 'celebracao' | 'countdown-final';

interface Props {
  nome:                string;
  duracaoCelebracaoSeg: number;
  onEnded:             () => void;
  onFaseChange?:       (fase: FaseOverlayAniversario) => void;
}

const CORES = ['#ff006e', '#fb5607', '#ffbe0b', '#3a86ff', '#8338ec', '#06d6a0', '#ffffff'];

const CONFETTI = Array.from({ length: 40 }, (_, i) => ({
  id:       i,
  left:     (i * 2.5) % 100,
  width:    8 + (i % 7),
  height:   10 + (i % 5),
  color:    CORES[i % CORES.length],
  delay:    (i * 0.1) % 4,
  duration: 3 + (i % 4),
}));

const NEON_FLICKER = { opacity: [1, 0.4, 1, 0.6, 1] };

export default function OverlayAniversario({ nome, duracaoCelebracaoSeg, onEnded, onFaseChange }: Props) {
  const reduzir = useReducedMotion();
  const onEndedRef = useRef(onEnded);
  useEffect(() => { onEndedRef.current = onEnded; }, [onEnded]);

  const onFaseChangeRef = useRef(onFaseChange);
  useEffect(() => { onFaseChangeRef.current = onFaseChange; }, [onFaseChange]);

  const [countdown, setCountdown] = useState(3);
  const [fase, setFase] = useState<FaseOverlayAniversario>('countdown-inicial');

  useEffect(() => {
    onFaseChangeRef.current?.(fase);
  }, [fase]);

  useEffect(() => {
    if (fase === 'countdown-inicial') {
      if (countdown === 0) {
        setFase('celebracao');
        return;
      }
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }

    if (fase === 'celebracao') {
      const t = setTimeout(() => {
        setCountdown(3);
        setFase('countdown-final');
      }, duracaoCelebracaoSeg * 1_000);
      return () => clearTimeout(t);
    }

    if (fase === 'countdown-final') {
      if (countdown === 0) {
        onEndedRef.current();
        return;
      }
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [fase, countdown, duracaoCelebracaoSeg]);

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden flex items-center justify-center">

      {/* Fundo Bokeh sobre o conteúdo atual */}
      <div className="absolute inset-0 backdrop-blur-2xl" style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}>
        {/* ponytail: 12 blur(40px) orbs — mais que isso pesa demais em TV Box ARM 24/7 */}
        {Array.from({ length: 12 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width:      `${80 + (i * 13) % 120}px`,
              height:     `${80 + (i * 13) % 120}px`,
              left:       `${(i * 17) % 100}%`,
              top:        `${(i * 23) % 100}%`,
              background: `radial-gradient(circle, ${CORES[i % CORES.length]}40 0%, transparent 70%)`,
              filter:     'blur(40px)',
            }}
            animate={reduzir ? undefined : { scale: [1, 1.2, 1], opacity: [0.6, 0.8, 0.6] }}
            transition={{ duration: 3 + (i % 4), delay: i * 0.3, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}
      </div>

      {/* Vinheta escura nas bordas */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse 70% 60% at 50% 45%, transparent 30%, rgba(0,0,0,0.82) 75%, rgba(0,0,0,0.96) 100%)',
        }}
      />

      {/* Confetti determinístico — sem Math.random() no render */}
      {!reduzir && CONFETTI.map(p => (
        <motion.div
          key={p.id}
          className="absolute top-0 pointer-events-none"
          style={{
            left:         `${p.left}%`,
            width:        `${p.width}px`,
            height:       `${p.height}px`,
            background:   p.color,
            borderRadius: '2px',
          }}
          initial={{ y: '-10%', rotate: 0, opacity: 1 }}
          animate={{ y: '110vh', rotate: 720, opacity: 0 }}
          transition={{ duration: p.duration, delay: p.delay, ease: 'easeIn', repeat: Infinity, repeatType: 'loop' }}
        />
      ))}

      {fase === 'countdown-inicial' && (
        <div className="relative z-10 flex flex-col items-center justify-center gap-6 select-none pointer-events-none">
          <p className="text-white/80 font-bold text-2xl uppercase tracking-widest">Preparando...</p>
          <motion.div
            className="font-black text-white tabular-nums"
            style={{
              fontSize:   'clamp(8rem, 20vw, 18rem)',
              textShadow: '0 0 20px #fff, 0 0 40px #ff006e, 0 0 80px #ff006e',
            }}
            animate={reduzir ? { opacity: 1 } : NEON_FLICKER}
            transition={{ duration: 0.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            {countdown}
          </motion.div>
        </div>
      )}

      {fase === 'celebracao' && (
        <div className="relative z-10 flex flex-col items-center gap-8 select-none pointer-events-none text-center px-8">
          <motion.p
            className="text-white font-black uppercase tracking-[0.25em]"
            style={{ fontSize: 'clamp(1.25rem, 2.5vw, 3rem)' }}
            animate={reduzir ? { opacity: 1 } : NEON_FLICKER}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          >
            🎂 Feliz Aniversário 🎂
          </motion.p>

          <motion.p
            className="font-black text-white leading-none"
            style={{
              fontSize:   'clamp(3rem, 8vw, 10rem)',
              textShadow: '0 0 7px #fff, 0 0 14px #fff, 0 0 28px #ff006e, 0 0 56px #ff006e, 0 0 100px #ff006e',
            }}
            animate={reduzir ? { opacity: 1 } : NEON_FLICKER}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          >
            {nome}
          </motion.p>

          <motion.p
            className="text-white/80 font-semibold"
            style={{ fontSize: 'clamp(1rem, 2vw, 2.5rem)' }}
            animate={reduzir ? { opacity: 1 } : NEON_FLICKER}
            transition={{ duration: 4, delay: 0.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            🎉 Parabéns pra você! 🎉
          </motion.p>
        </div>
      )}

      {fase === 'countdown-final' && (
        <div className="relative z-10 flex flex-col items-center justify-center gap-6 select-none pointer-events-none">
          <p className="text-white/80 font-bold text-2xl uppercase tracking-widest">Encerrando em...</p>
          <motion.div
            className="font-black text-white tabular-nums"
            style={{
              fontSize:   'clamp(8rem, 20vw, 18rem)',
              textShadow: '0 0 20px #fff, 0 0 40px #3a86ff, 0 0 80px #3a86ff',
            }}
            animate={reduzir ? { opacity: 1 } : NEON_FLICKER}
            transition={{ duration: 0.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            {countdown}
          </motion.div>
        </div>
      )}
    </div>
  );
}
